import React, { useEffect, useState, useCallback, useRef } from "react";
import { getStandalonePaymentDetails, patchOnceOffPayment } from "../DataSource";
import DropIn from "../components/Braintree/DropIn";
import LoadingOverlay from "../components/LoadingOverlay";
import Error from "../components/Error";
import Checkbox from '../elements/Checkbox'

const StandalonePayments = props => {
    const METHOD_NONCE = "braintreeNonce";
    const [dropInInstance, setDropInInstance] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hash, setHash] = useState(props.match.params.hash);
    const [customerId, setCustomerId] = useState(null);
    const [overdueAmountCents, setOverdueAmountCents] = useState(0);
    const [updatePaymentMethod, setUpdatePaymentMethod] = useState(false);
    const [validationErrors, setValidationErrors] = useState(null);
    const [displaySuccessMessage, setDisplaySuccessMessage] = useState(false);
    const [alreadyPaidMessage, setAlreadyPaidMessage] = useState('')
    const [isPaymentDone, setIsPaymentDone] = useState(false)
    const [hasWrongPaymentLink, setHasWrongPaymentLink] = useState(false)
    const [dropInKey, setDropInKey] = useState(0)
    const errorRef = useRef(null);

    const handleSubmitEvent = data => {
        if (dropInInstance) {
            dropInInstance.requestPaymentMethod(function(err, payload) {
                if (err) return;
                data.paymentMethod = METHOD_NONCE;
                data.identifierType = "nonce";
                data.identifier = payload.nonce;
                submitOrder(data);
            });
        }
    };

    const maskedAmount = (amount) => {
        return Number(amount/100).toFixed(2)
    }

    const submitOrder = async (data) => {
        setIsLoading(true);
        data.completed = true;
        data.page = 'StandalonePayment';
        data.amount = overdueAmountCents;
        const paymentResponse = await patchOnceOffPayment({
            nonce: hash,
            braintreeNonce: data.identifier,
            updatePaymentMethod: updatePaymentMethod,
            amountCents: overdueAmountCents
        })
        setIsLoading(false);
        if (paymentResponse.response && paymentResponse.response.status >= 400) {
            setValidationErrors(paymentResponse.response.data.message)
            scrollToError()
            setDropInKey(dropInKey ? 0 : 1)
        }
        if (paymentResponse && paymentResponse.status === 200) {
            setDisplaySuccessMessage(true)
        }
    }

    const handleDropInInstance = instance => {
        setDropInInstance(instance);
        setIsLoading(false);
    };

    const getData = async () => {
        const response = await getStandalonePaymentDetails(hash);
        // 410 Status - Payment already done
        if (response && response.response && response.response.status === 410) {
            setOverdueAmountCents(0);
            setIsPaymentDone(true);
            setAlreadyPaidMessage(response.response.data.error)
        }
        // 40x Status - Other error types
        if (response && response.response && response.response.status >= 400 && response.response.status !== 410) {
            setOverdueAmountCents(0)
            setHasWrongPaymentLink(true)
            setIsPaymentDone(true)
        }
        if (response.data) {
            setCustomerId(response.data.customerId);
            setOverdueAmountCents(response.data.overdueAmountCents);
            if (response.data.overdueAmountCents === 0) {
                setUpdatePaymentMethod(true)
            }
        }
    };

    const showError = useCallback(() => {
        return validationErrors ?
            <div className="row mt-3" ref={errorRef}>
                <div className="col">
                    <Error errors={validationErrors} />
                </div>
            </div>
            : ''
    }, [validationErrors])

    useEffect(
        () => {
            getData();
        },
        [props.history, props.match.params.hash]
    );

    const scrollToError = () => {
        if (errorRef.current) {
            errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    };

    return (
        <div className="standalone-payments">
            <LoadingOverlay isLoading={isLoading}>
                {overdueAmountCents === 0 ? <div className="d-block bold mb-2 update-payment">You currently have $0 outstanding. You may use this form to update your credit card details.</div> : ""}
                <div className="div-outer-box">
                    {overdueAmountCents === 0 ? "" : <div className="div-inner-box">
                        <label className="d-block bold label">Amount to pay</label>
                        <input
                            className='bg-lightgrey w-100 input-standalone-payment'
                            disabled
                            placeholder="$0"
                            value={'$' + maskedAmount(overdueAmountCents)}
                        />
                    </div>}
                    <div className="div-inner-box">
                        <label className="d-block label">Customer ID</label>
                        <input
                            className="bg-lightgrey w-100 input-standalone-payment"
                            disabled
                            placeholder="eg. 123"
                            value={customerId}
                        />
                    </div>
                </div>
                {showError()}
                <div>
                    <DropIn handleDropInInstance={handleDropInInstance} key={dropInKey}>
                        <div className="div-button">
                            <Checkbox
                                value={updatePaymentMethod}
                                handleClick={()=> setUpdatePaymentMethod(!updatePaymentMethod)}
                                name='updatePaymentMethod'
                            >
                                Save this as my default payment method
                            </Checkbox>
                            <button className={`btn btn-custom ${displaySuccessMessage || isPaymentDone || (overdueAmountCents === 0 && !updatePaymentMethod) ? 'disabled' : ''}`} disabled={displaySuccessMessage || isPaymentDone || (overdueAmountCents === 0 && !updatePaymentMethod)} onClick={handleSubmitEvent} type="button">
                                <span className="text">{overdueAmountCents === 0 ? 'Update your details' : 'Submit once-off payment'} {">"}</span>
                            </button>
                        </div>
                    </DropIn>
                    {hasWrongPaymentLink ?
                        <div className="row mb-4">
                            <div className="col">
                                <div className='row-error my-2'>
                                    Invalid payment link, please check the link is correct and retry, or <a href="https://www.exetel.com.au/contact-us" className="bold underline color-grey">contact us</a> for assistance making your payment.
                                </div>
                            </div>
                        </div>
                        : ''}
                    {displaySuccessMessage ?
                        <div className="row mb-4">
                            <div className="col">
                                <p className="successful-payment">
                                    Thank you, your payment has been received successfully
                                    {updatePaymentMethod ? " and your payment details updated." : "."}
                                </p>
                            </div>
                        </div>
                        : ''}
                    {alreadyPaidMessage ?
                        <div className="row mb-4">
                            <div className="col">
                                <p className="successful-payment">
                                    {alreadyPaidMessage}
                                </p>
                            </div>
                        </div>
                        : ''}    
                </div>
            </LoadingOverlay>
        </div>
    );
};
export { StandalonePayments };


// WEBPACK FOOTER //
// ./resources/js/pages/StandalonePayments.js